/* Reading newline-separated JSON as it arrives.
 *
 * Every server function here streams its answer, because a Netlify function
 * that replies in one go is given ten seconds and anything involving a model
 * takes longer. The shape is always the same: a start, a pulse every few
 * seconds, then exactly one done or one error.
 *
 * A network chunk has nothing to do with a line. One chunk can carry three
 * lines or half of one, and the half that is left over has to survive until
 * the rest turns up. Getting that wrong throws a parse error on perfectly
 * good data, and only sometimes, which is the worst kind of bug.
 */

export async function* jsonLines(response) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let held = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    /* stream: true, or a multi-byte character split across two chunks decodes
       to a replacement character and a quote comes out wrong in the report. */
    held += decoder.decode(value, { stream: true });
    const lines = held.split("\n");
    held = lines.pop() ?? "";
    for (const l of lines) {
      const trimmed = l.trim();
      if (trimmed) yield JSON.parse(trimmed);
    }
  }
  const last = held.trim();
  if (last) yield JSON.parse(last);
}

/* The whole exchange: send, read the stream, hand back the one done payload.
 * Named for what goes wrong rather than what goes right, because every branch
 * here is a failure somebody has to be able to act on. */
export async function askStreaming(endpoint, { token, body, onProgress, what = "That" }) {
  let response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Could not reach the service. Check your connection and try again.");
  }

  /* A function that is missing entirely answers with the site's index page,
     which is HTML, and the reader would fail on the angle bracket. */
  const type = response.headers.get("content-type") || "";
  if (!response.body || !/json/i.test(type)) {
    const text = await response.text().catch(() => "");
    if (response.status === 404 || /<!doctype html/i.test(text)) {
      throw new Error(
        "That service is not on this site. The function did not deploy. "
        + "Check the deploy log on Netlify.",
      );
    }
    throw new Error(
      `The service failed before it could answer (status ${response.status}). `
      + `It said: ${text.slice(0, 200) || "nothing at all"}`,
    );
  }

  let found = null;
  for await (const event of jsonLines(response)) {
    if (event.error && !event.type) throw new Error(event.error);
    if (event.type === "working") onProgress?.();
    if (event.type === "error") throw new Error(event.error);
    if (event.type === "done") found = event;
  }

  /* A stream that ends without a verdict was cut off partway, which on Netlify
     means it ran past its sixty seconds. Say that, because the answer is a
     smaller job rather than another try. */
  if (!found) {
    throw new Error(
      `${what} stopped before it finished, which usually means there was too `
      + "much of it. Try a smaller file, or split it in two.",
    );
  }
  return found;
}
